import { useEffect, useMemo, useState, useTransition } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { usePassengersActions, useRoutingServices } from "@ryde/redux";
import {
  errors,
  getIsValidStationLocation,
  ICoordinate,
  ILocationInfo,
  IPassengerStationLocationRequestDto,
  IPassengerStationLocationResponseDto,
  IWarkDistanceAndTime,
  Log,
  warnAndToastException,
  warnAndToastSimpleResult,
} from "@ryde/shared";

export const usePassengerAddress = () => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const { searchPassengerStationsLocations } = usePassengersActions();
  const { getWalkingDistanceAndTime } = useRoutingServices();
  const [, startTransition] = useTransition();

  const {
    sourceStation1Id,
    sourceStation2Id,
    destinationStation1Id,
    destinationStation2Id,
    metadata,
  } = getValues();
  const { sourceStation3Id, destinationStation3Id } = metadata || {};

  const [locations, setLocations] =
    useState<IPassengerStationLocationResponseDto | null>(null);
  const [distanceAndTime, setDistanceAndTime] =
    useState<IWarkDistanceAndTime | null>(null);

  const activeLocations = useMemo<ILocationInfo[]>(
    () =>
      [
        locations?.sourceStation1Location,
        locations?.sourceStation2Location,
        locations?.sourceStation3Location,
        locations?.destinationStation1Location,
        locations?.destinationStation2Location,
        locations?.destinationStation3Location,
      ].filter((location): location is ILocationInfo => Boolean(location)),
    [locations],
  );

  const fetchWalkingDistanceAndTime = useEvent(
    async (origin: ICoordinate, destination: ICoordinate): Promise<void> => {
      try {
        const distanceAndTimeResult = await getWalkingDistanceAndTime({
          origin,
          destination,
        });

        if (!distanceAndTimeResult.success || !distanceAndTimeResult.data) {
          return warnAndToastSimpleResult(distanceAndTimeResult, t);
        }

        const data = distanceAndTimeResult.data;
        setDistanceAndTime(data);
        const newWalkingDistance = String(data.distance);
        const currentWalkingDistance =
          getValues("metadata.walkingDistance") ?? "";
        if (newWalkingDistance !== currentWalkingDistance) {
          setValue("metadata.walkingDistance", newWalkingDistance, {
            shouldDirty: true,
          });
        }
      } catch (error) {
        warnAndToastException({
          error,
          fallbackTranslationKey: errors.common.technicalProblemOccurred,
          translationFunction: t,
          logWarnMessage: "Error while run fetchWalkingDistanceAndTime method",
        });
      }
    },
  );

  const searchLocations = useEvent(async () => {
    const payload: IPassengerStationLocationRequestDto = {
      sourceStation1Id,
      sourceStation2Id,
      sourceStation3Id,
      destinationStation1Id,
      destinationStation2Id,
      destinationStation3Id,
    };

    const hasAnyStationId = [
      sourceStation1Id,
      sourceStation2Id,
      sourceStation3Id,
      destinationStation1Id,
      destinationStation2Id,
      destinationStation3Id,
    ].some(Number);

    if (!hasAnyStationId) {
      return;
    }

    try {
      const locationsResult = await searchPassengerStationsLocations(payload);

      if (!locationsResult.success || !locationsResult.data) {
        Log.warnAndToast(
          t(errors.booking.failedToFetchPassengerStations),
          locationsResult.error,
        );
        return;
      }

      const { sourceStation1Location, sourceStation2Location } =
        locationsResult.data;

      setLocations(locationsResult.data);

      if (
        getIsValidStationLocation(sourceStation1Location) &&
        getIsValidStationLocation(sourceStation2Location)
      ) {
        fetchWalkingDistanceAndTime(
          {
            lat: sourceStation1Location.latitude,
            lng: sourceStation1Location.longitude,
          },
          {
            lat: sourceStation2Location.latitude,
            lng: sourceStation2Location.longitude,
          },
        );
      }
    } catch (error) {
      warnAndToastException({
        error,
        fallbackTranslationKey: errors.common.technicalProblemOccurred,
        translationFunction: t,
        logWarnMessage: "Error while run searchLocations method",
      });
    }
  });

  useEffect(() => {
    startTransition(() => {
      searchLocations();
    });
  }, [
    sourceStation1Id,
    sourceStation2Id,
    sourceStation3Id,
    destinationStation1Id,
    destinationStation2Id,
    destinationStation3Id,
    searchLocations,
  ]);

  const walkPositions = useMemo(
    () => (distanceAndTime?.path ?? []) as [number, number][],
    [distanceAndTime],
  );

  return {
    locations,
    distanceAndTime,
    walkPositions,
    activeLocations,
  };
};
